import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import Supercluster from 'supercluster';
import { ProfileDialogService } from '../services/profile-dialog.service';
import { ChatLink, MapMember, MapSpace } from '../data-transfer-objects/member-map.dto';
import {
  HtmlOverlay,
  MAP_STYLE,
  buildClusterPin,
  buildSearchPin,
  buildSpacePin,
  buildUserPin,
  createHtmlOverlay,
  distanceKm,
  distanceLabel,
  NEW_WINDOW_MS,
  timeAgo,
} from './map-pins';

type PinProps = { id: string };
export type MapView = { lat: number; lng: number; zoom: number };
type Pos = google.maps.LatLngLiteral;

/** Chats this recent draw in teal, older ones in grey. */
const RECENT_CHAT_MS = 30 * 24 * 60 * 60 * 1000;
const WORLD: [number, number, number, number] = [-180, -85, 180, 85];

/**
 * The admin members map.
 *
 * Members are clustered with supercluster and drawn as HTML overlays (see
 * map-pins.ts). Only pins inside the padded viewport exist in the DOM, and
 * they are kept across pans so photos do not reload. Chat links and a
 * selected space's spokes are polylines between what is actually drawn - a
 * member folded into a cluster is linked from the cluster.
 */
@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  imports: [
    CommonModule,
    GoogleMapsModule,
    AsyncPipe,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
  ],
  standalone: true,
})
export class MapComponent implements OnChanges, OnDestroy {
  @Input() members: MapMember[] = [];
  @Input() spaces: MapSpace[] = [];
  /** null while the chat layer is off. */
  @Input() chatLinks: ChatLink[] | null = null;
  @Input() selectedSpaceId: string | null = null;
  /** Joined within this counts as new: the page's timeframe. */
  @Input() newWithinMs: number = NEW_WINDOW_MS;

  /** "Show its members" was chosen on a space. */
  @Output() spaceSelected = new EventEmitter<MapSpace>();
  /** "Back to all members" was chosen on the picked space. */
  @Output() spaceCleared = new EventEmitter<void>();
  /** A chat line was clicked; every pair it stands for, most messages first. */
  @Output() chatLinkClicked = new EventEmitter<ChatLink[]>();

  /** Where the map settles after a pan or zoom, for the page to keep in the URL. */
  @Output() viewChanged = new EventEmitter<MapView>();
  /** The view to open on, from the URL; it wins over fitting to a space. */
  @Input() initialView: MapView | null = null;

  @ViewChild('chatTip') chatTip?: ElementRef<HTMLElement>;
  @ViewChild('frame') frame?: ElementRef<HTMLElement>;

  clickedLocation?: Pos;
  /** The action card a space pin opened, placed over the pin. */
  spaceMenu: { space: MapSpace; x: number; y: number; below: boolean } | null = null;

  options: google.maps.MapOptions = {
    center: { lat: 47.38198713379071, lng: 8.496863489770712 },
    zoom: 4,
    clickableIcons: false,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
    styles: MAP_STYLE,
  };

  apiLoaded$: Observable<boolean>;

  private map?: google.maps.Map;
  private index?: Supercluster<PinProps>;
  private byId = new Map<string, MapMember>();
  private pins = new Map<string, { overlay: HtmlOverlay; el: HTMLElement }>();
  private spaceOverlays: HtmlOverlay[] = [];
  private lines: google.maps.Polyline[] = [];
  private searchOverlay?: HtmlOverlay;
  private fitPending = false;
  /** Set by an initial view from the URL: the first fit must not undo it. */
  private keepInitialView = false;
  /** The member the search flew to, highlighted until the map is touched. */
  private focusId: string | null = null;

  constructor(
    private httpClient: HttpClient,
    private zone: NgZone,
    private profileDialog: ProfileDialogService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {
    // Don't load the api multiple times
    if (window.google?.maps) {
      this.apiLoaded$ = of(true);
      return;
    }
    this.apiLoaded$ = this.httpClient
      .jsonp(
        `https://maps.googleapis.com/maps/api/js?key=${environment.firebase.apiKey}`,
        'callback'
      )
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialView'] && this.initialView && !this.map) {
      const v = this.initialView;
      this.options = { ...this.options, center: { lat: v.lat, lng: v.lng }, zoom: v.zoom };
      this.keepInitialView = true;
    }
    if (changes['selectedSpaceId'] && this.selectedSpaceId) this.fitPending = true;
    // Pins bake the new ring in when built, so a new window redraws them.
    if (changes['members'] || changes['newWithinMs']) this.buildIndex();
    if (changes['spaces'] || changes['selectedSpaceId']) this.renderSpaces();
    if (changes['members'] && this.fitPending && this.selectedSpaceId) this.fitToSelection();
    this.render();
  }

  onMapReady(mapInstance: google.maps.Map): void {
    this.map = mapInstance;
    this.renderSpaces();
    this.renderSearchPin();
    // The first idle event draws the pins once the map knows its bounds.
  }

  onIdle(): void {
    this.render();
    const c = this.map?.getCenter();
    if (c) {
      this.viewChanged.emit({
        lat: Math.round(c.lat() * 1e4) / 1e4,
        lng: Math.round(c.lng() * 1e4) / 1e4,
        zoom: this.zoom(),
      });
    }
  }

  onMapClick(event: google.maps.MapMouseEvent | google.maps.IconMouseEvent) {
    // A click away from an open space card only closes it; dropping a search
    // point there as well would be a surprise.
    this.clearFocus();
    if (this.spaceMenu) {
      this.spaceMenu = null;
      return;
    }
    this.clickedLocation = event.latLng?.toJSON();
    this.renderSearchPin();
  }

  clearSearch(): void {
    this.clickedLocation = undefined;
    this.renderSearchPin();
  }

  ngOnDestroy(): void {
    this.pins.forEach((p) => p.overlay.setMap(null));
    this.spaceOverlays.forEach((o) => o.setMap(null));
    this.lines.forEach((l) => l.setMap(null));
    this.searchOverlay?.setMap(null);
  }

  /* ---------- data ---------- */

  private buildIndex(): void {
    this.byId = new Map(this.members.map((m) => [m.id, m]));
    const index = new Supercluster<PinProps>({ radius: 80, maxZoom: 15, minPoints: 2 });
    index.load(
      this.members.map((m) => ({
        type: 'Feature' as const,
        properties: { id: m.id },
        geometry: { type: 'Point' as const, coordinates: [m.lng, m.lat] },
      }))
    );
    this.index = index;
    // Positions and cluster ids all change with the data; start clean.
    this.pins.forEach((p) => p.overlay.setMap(null));
    this.pins.clear();
  }

  private zoom(): number {
    return Math.round(this.map?.getZoom() ?? 4);
  }

  /** The viewport plus a margin, as supercluster's [w, s, e, n]. */
  private bbox(): [number, number, number, number] {
    const b = this.map?.getBounds();
    if (!b || this.zoom() <= 3) return WORLD;
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    if (sw.lng() > ne.lng()) return WORLD; // straddles the antimeridian
    const padLng = (ne.lng() - sw.lng()) * 0.25;
    const padLat = (ne.lat() - sw.lat()) * 0.25;
    return [
      Math.max(-180, sw.lng() - padLng),
      Math.max(-85, sw.lat() - padLat),
      Math.min(180, ne.lng() + padLng),
      Math.min(85, ne.lat() + padLat),
    ];
  }

  /* ---------- drawing ---------- */

  private render(): void {
    if (!this.map || !this.index || !this.map.getBounds()) return;
    const zoom = this.zoom();
    const features = this.index.getClusters(this.bbox(), zoom);

    // Southern pins paint over northern ones, so a tail is never hidden
    // behind the photo of the pin just below it.
    features.sort((a, b) => b.geometry.coordinates[1] - a.geometry.coordinates[1]);

    const wanted = new Set<string>();
    features.forEach((f, i) => {
      const [lng, lat] = f.geometry.coordinates;
      const props = f.properties as PinProps & Partial<Supercluster.ClusterProperties>;
      const key = props.cluster ? `c${f.id}` : `u${props.id}`;
      wanted.add(key);
      let pin = this.pins.get(key);
      if (!pin) {
        const el = props.cluster
          ? this.clusterElement(Number(f.id), props.point_count ?? 0, { lat, lng })
          : this.memberElement(this.byId.get(props.id)!);
        const overlay = createHtmlOverlay({ lat, lng }, el);
        overlay.setMap(this.map!);
        pin = { overlay, el };
        this.pins.set(key, pin);
      }
      pin.el.style.zIndex = String(i + 1);
      pin.el.classList.toggle('nx-pin--focus', key === `u${this.focusId}`);
    });

    for (const [key, pin] of this.pins) {
      if (!wanted.has(key)) {
        pin.overlay.setMap(null);
        this.pins.delete(key);
      }
    }

    this.renderLines(zoom);
  }

  private memberElement(m: MapMember): HTMLElement {
    return buildUserPin(m, {
      onClick: () => this.zone.run(() => this.openMember(m.id)),
      newWithinMs: this.newWithinMs,
      distance: () =>
        this.clickedLocation
          ? distanceLabel(distanceKm(this.clickedLocation, { lat: m.lat, lng: m.lng }))
          : null,
    });
  }

  private clusterElement(clusterId: number, count: number, at: Pos): HTMLElement {
    const sample = this.index!
      .getLeaves(clusterId, 2)
      .map((leaf) => this.byId.get(leaf.properties.id))
      .filter((m): m is MapMember => !!m);
    return buildClusterPin(sample, count, () => {
      const target = Math.min(this.index!.getClusterExpansionZoom(clusterId), 17);
      this.map?.setZoom(target);
      this.map?.panTo(at);
    });
  }

  /**
   * Where each member is drawn at this zoom: its own point, or the cluster it
   * is folded into. Worldwide, because a line's far end is often off-screen.
   */
  private drawnAt(zoom: number): Map<string, { key: string; pos: Pos }> {
    const out = new Map<string, { key: string; pos: Pos }>();
    for (const f of this.index!.getClusters(WORLD, zoom)) {
      const [lng, lat] = f.geometry.coordinates;
      const props = f.properties as PinProps & Partial<Supercluster.ClusterProperties>;
      if (props.cluster) {
        const node = { key: `c${f.id}`, pos: { lat, lng } };
        for (const leaf of this.index!.getLeaves(Number(f.id), Infinity)) {
          out.set(leaf.properties.id, node);
        }
      } else {
        out.set(props.id, { key: `u${props.id}`, pos: { lat, lng } });
      }
    }
    return out;
  }

  private renderLines(zoom: number): void {
    this.lines.forEach((l) => l.setMap(null));
    this.lines = [];
    if (!this.map || !this.index) return;
    const selected = this.spaces.find((s) => s.id === this.selectedSpaceId);
    /*
     * One kind of line at a time. The spokes from a picked space to its
     * members drown the chat lines between those same members, so while the
     * chat layer is on the spokes step aside; the space pin stays.
     */
    const hubbed =
      !this.chatLinks && selected && selected.lat != null && selected.lng != null
        ? selected
        : undefined;
    if (!this.chatLinks && !hubbed) return;

    const drawn = this.drawnAt(zoom);

    if (this.chatLinks) {
      // One line per pair of drawn nodes: two clusters with five chats
      // between them are one thicker line, not five on top of each other.
      const bundles = new Map<string, { a: Pos; b: Pos; links: ChatLink[] }>();
      for (const link of this.chatLinks) {
        const a = drawn.get(link.a);
        const b = drawn.get(link.b);
        if (!a || !b || a.key === b.key) continue;
        const key = a.key < b.key ? `${a.key}|${b.key}` : `${b.key}|${a.key}`;
        const bundle = bundles.get(key);
        if (bundle) bundle.links.push(link);
        else bundles.set(key, { a: a.pos, b: b.pos, links: [link] });
      }
      const now = Date.now();
      for (const bundle of bundles.values()) {
        const links = bundle.links.sort((x, y) => y.messages - x.messages);
        const messages = links.reduce((n, l) => n + l.messages, 0);
        const recent = links.some(
          (l) => !!l.lastMessageAt && now - Date.parse(l.lastMessageAt) <= RECENT_CHAT_MS
        );
        const blocked = links.every((l) => l.blocked);
        const color = blocked ? '#A32D2D' : recent ? '#1f6f66' : '#909f9d';
        const weight = Math.min(1.5 + Math.log2(1 + messages) * 0.8, 6);
        const opacity = recent ? 0.75 : 0.5;
        const visible = new google.maps.Polyline({
          map: this.map,
          path: [bundle.a, bundle.b],
          geodesic: true,
          clickable: false,
          strokeColor: color,
          strokeOpacity: opacity,
          strokeWeight: weight,
          zIndex: 1,
        });
        // A 2px line is too thin to hover; this wide, nearly invisible twin
        // takes the pointer instead.
        const hit = new google.maps.Polyline({
          map: this.map,
          path: [bundle.a, bundle.b],
          geodesic: true,
          clickable: true,
          strokeColor: color,
          strokeOpacity: 0.01,
          strokeWeight: 16,
          zIndex: 3,
        });
        hit.addListener('mouseover', (e: google.maps.PolyMouseEvent) => {
          visible.setOptions({ strokeWeight: weight + 3, strokeOpacity: 1 });
          this.showChatTip(links, e.domEvent as MouseEvent);
        });
        hit.addListener('mousemove', (e: google.maps.PolyMouseEvent) =>
          this.moveChatTip(e.domEvent as MouseEvent)
        );
        hit.addListener('mouseout', () => {
          visible.setOptions({ strokeWeight: weight, strokeOpacity: opacity });
          this.hideChatTip();
        });
        hit.addListener('click', () => {
          this.hideChatTip();
          this.zone.run(() => this.chatLinkClicked.emit(links));
        });
        this.lines.push(visible, hit);
      }
    }

    if (hubbed) {
      const hub = { lat: hubbed.lat!, lng: hubbed.lng! };
      const ends = new Map<string, Pos>();
      for (const id of hubbed.memberIds) {
        const node = drawn.get(id);
        if (node) ends.set(node.key, node.pos);
      }
      for (const end of ends.values()) {
        this.lines.push(
          new google.maps.Polyline({
            map: this.map,
            path: [hub, end],
            geodesic: true,
            clickable: false,
            strokeOpacity: 0,
            icons: [
              {
                icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.7, strokeColor: '#12332f', scale: 2 },
                offset: '0',
                repeat: '10px',
              },
            ],
            zIndex: 2,
          })
        );
      }
    }
  }

  private renderSpaces(): void {
    this.spaceOverlays.forEach((o) => o.setMap(null));
    this.spaceOverlays = [];
    if (!this.map) return;
    this.spaces.forEach((s, i) => {
      if (s.lat == null || s.lng == null) return;
      const el = buildSpacePin(s, s.id === this.selectedSpaceId, () =>
        this.zone.run(() => this.openSpaceMenu(s, el))
      );
      el.style.zIndex = String(50000 + i);
      const overlay = createHtmlOverlay({ lat: s.lat, lng: s.lng }, el);
      overlay.setMap(this.map!);
      this.spaceOverlays.push(overlay);
    });
  }

  private fitToSelection(): void {
    if (this.keepInitialView) {
      this.keepInitialView = false;
      this.fitPending = false;
      return;
    }
    const selected = this.spaces.find((s) => s.id === this.selectedSpaceId);
    if (!this.map || (selected?.lat == null && !this.members.length)) return;
    const bounds = new google.maps.LatLngBounds();
    if (selected?.lat != null && selected.lng != null) {
      bounds.extend({ lat: selected.lat, lng: selected.lng });
    }
    this.members.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    this.map.fitBounds(bounds, 64);
    this.fitPending = false;
  }

  private renderSearchPin(): void {
    this.searchOverlay?.setMap(null);
    this.searchOverlay = undefined;
    if (!this.map || !this.clickedLocation) return;
    this.searchOverlay = createHtmlOverlay(this.clickedLocation, buildSearchPin());
    this.searchOverlay.setMap(this.map);
  }

  /* ---------- space action card ---------- */

  private openSpaceMenu(space: MapSpace, pin: HTMLElement): void {
    const frame = this.frame?.nativeElement;
    if (!frame) return;
    const box = frame.getBoundingClientRect();
    const at = pin.getBoundingClientRect();
    const x = Math.min(Math.max(at.left + at.width / 2 - box.left, 140), box.width - 140);
    // Open below the pin when there is no room for the card above it.
    const below = at.top - box.top < 190;
    const y = below ? at.bottom - box.top + 8 : at.top - box.top - 8;
    this.spaceMenu = { space, x, y, below };
  }

  closeSpaceMenu(): void {
    this.spaceMenu = null;
  }

  isSelected(space: MapSpace): boolean {
    return space.id === this.selectedSpaceId;
  }

  filterBySpace(space: MapSpace): void {
    this.spaceMenu = null;
    this.spaceSelected.emit(space);
  }

  clearSpace(): void {
    this.spaceMenu = null;
    this.spaceCleared.emit();
  }

  /**
   * The admin space page, the same one every board links to. In a new tab,
   * so the map keeps its filters, zoom and layers.
   *
   * Deliberately without "noopener". Admin sign-in uses session persistence
   * (login.component.ts), which lives in the tab's sessionStorage, and a
   * browser copies that into a tab opened by script only while the opener
   * link exists. With noopener the new tab started signed out and the guard
   * sent it to the login page. The page opened is our own, same origin.
   */
  openSpacePage(space: MapSpace): void {
    this.spaceMenu = null;
    const url = this.router.serializeUrl(this.router.createUrlTree(['/admin/spaces', space.id]));
    window.open(url, '_blank');
  }

  spaceDates(space: MapSpace): string | null {
    if (!space.startDate) return null;
    const fmt = (d: string) =>
      new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    return space.endDate && fmt(space.endDate) !== fmt(space.startDate)
      ? `${fmt(space.startDate)} – ${fmt(space.endDate)}`
      : fmt(space.startDate);
  }

  /**
   * Fly to one member and hold their card open: the search box's answer.
   * Zooms only as far as it takes for them to be their own pin rather than
   * part of a cluster.
   */
  focusMember(id: string): void {
    const m = this.byId.get(id);
    if (!this.map || !this.index || !m) return;
    const pad = 0.0005;
    let zoom = Math.max(this.zoom(), 8);
    for (; zoom < 16; zoom++) {
      const near = this.index.getClusters([m.lng - pad, m.lat - pad, m.lng + pad, m.lat + pad], zoom);
      if (near.some((f) => !(f.properties as any).cluster && f.properties.id === id)) break;
    }
    this.focusId = id;
    this.map.setZoom(zoom);
    this.map.panTo({ lat: m.lat, lng: m.lng });
    this.render();
    // Once the pin is drawn, open its card as a hover would, placement included.
    setTimeout(() => {
      this.pins.get(`u${id}`)?.el.dispatchEvent(new MouseEvent('mouseenter'));
    }, 400);
  }

  clearFocus(): void {
    if (!this.focusId) return;
    this.pins.get(`u${this.focusId}`)?.el.classList.remove('nx-pin--focus');
    this.focusId = null;
  }

  /** Fit the view to these members, as the chat popup asks for. */
  focusMembers(ids: string[]): void {
    const found = ids.map((id) => this.byId.get(id)).filter((m): m is MapMember => !!m);
    if (!this.map || !found.length) return;
    if (found.length === 1) {
      this.map.panTo({ lat: found[0].lat, lng: found[0].lng });
      this.map.setZoom(Math.max(this.zoom(), 12));
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    found.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    this.map.fitBounds(bounds, 96);
  }

  /* ---------- chat line tooltip ---------- */

  private nameOf(id: string): string {
    const m = this.byId.get(id);
    return m ? `${m.firstName} ${m.lastName}`.trim() || 'Member' : 'Member';
  }

  /*
   * Plain DOM rather than a template binding: mousemove fires dozens of
   * times a second and has no business running change detection.
   */
  private showChatTip(links: ChatLink[], e: MouseEvent): void {
    const tip = this.chatTip?.nativeElement;
    if (!tip) return;
    tip.replaceChildren();
    const row = (cls: string, text: string) => {
      const d = document.createElement('div');
      d.className = cls;
      d.textContent = text;
      tip.appendChild(d);
    };
    if (links.length > 1) row('chat-tip__head', `${links.length} chats on this line`);
    for (const l of links.slice(0, 5)) {
      const starter = l.initiator === l.b ? l.b : l.a;
      const other = starter === l.a ? l.b : l.a;
      row('chat-tip__pair', `${this.nameOf(starter)} → ${this.nameOf(other)}`);
      const when = timeAgo(l.lastMessageAt);
      row(
        'chat-tip__meta',
        [
          `${l.messages} message${l.messages === 1 ? '' : 's'}`,
          l.perSender[other] ? 'replied' : 'no reply',
          when ? `last ${when}` : null,
          l.blocked ? 'blocked' : null,
        ]
          .filter(Boolean)
          .join(' · ')
      );
    }
    if (links.length > 5) row('chat-tip__more', `and ${links.length - 5} more`);
    row('chat-tip__hint', 'Click for details');
    tip.style.display = 'block';
    this.moveChatTip(e);
  }

  private moveChatTip(e: MouseEvent): void {
    const tip = this.chatTip?.nativeElement;
    const frame = this.frame?.nativeElement;
    if (!tip || !frame || !e) return;
    const box = frame.getBoundingClientRect();
    let x = e.clientX - box.left + 14;
    let y = e.clientY - box.top + 14;
    if (x + tip.offsetWidth > box.width - 8) x = e.clientX - box.left - tip.offsetWidth - 14;
    if (y + tip.offsetHeight > box.height - 8) y = e.clientY - box.top - tip.offsetHeight - 14;
    tip.style.transform = `translate(${Math.max(8, x)}px, ${Math.max(8, y)}px)`;
  }

  private hideChatTip(): void {
    const tip = this.chatTip?.nativeElement;
    if (tip) tip.style.display = 'none';
  }

  private openMember(uid: string): void {
    this.profileDialog.open(uid).catch((err: Error) =>
      this.snackBar.open(err.message, 'OK', { duration: 6000 })
    );
  }
}
